import { useMemo, useCallback, type MouseEvent, type ReactNode } from 'react'
import { useAtomValue } from 'jotai'
import { Button, ButtonGroup, Menu } from '@highlight/ui'
import { ChevronDown, Play, RefreshCw } from 'lucide-react'
import { useConnectProvider } from '@renderer/hooks/use-connect-provider'
import { useConnections, type Connection } from '@renderer/hooks/use-connections'
import { CONNECTION_ERROR_CODES, type StagedAction } from '../hooks/use-staged-actions'
import { invokeErrorByIdAtom, isInvokingByIdAtom, type InvokeErrorEntry } from '../hooks/staged-action-local-state'
import { useStagedActionCallbacks } from '../hooks/use-staged-action-view'
import { FLOATING_DANGER_FILL_CLASS } from '@renderer/components/floating-surface'

export function useConnectionError(action: StagedAction): boolean {
  const invokeError = useAtomValue(invokeErrorByIdAtom(action.id))
  const errorCode = invokeError?.errorCode || null
  return !!errorCode && CONNECTION_ERROR_CODES.has(errorCode)
}

type ConnectionAction = 'retry' | 'reconnect'

const ACTION_CONFIG: Record<ConnectionAction, { label: string; icon: ReactNode }> = {
  retry: { label: 'Retry', icon: <Play fill="currentColor" className="size-2 opacity-80" /> },
  reconnect: { label: 'Reconnect', icon: <RefreshCw className="size-2" /> },
}

function getLatestConnectionUpdatedAt(
  connections: Array<Connection> | undefined,
  providerId: string | null,
): number | null {
  if (!providerId || !connections) return null

  return connections
    .filter((connection) => connection.providerName === providerId && connection.updatedAt)
    .reduce<number | null>((latest, connection) => {
      const ts = Date.parse(connection.updatedAt)
      if (!Number.isNaN(ts) && (latest === null || ts > latest)) {
        return ts
      }
      return latest
    }, null)
}

function getActionErrorUpdatedAt(action: StagedAction, invokeError: InvokeErrorEntry | undefined): number | null {
  // Prefer the in-memory error timestamp — onError rolls the cache back so server lastInvokedAt
  // doesn't reflect the failed attempt. Fall back to action timestamps after a reload.
  const fromAtom = invokeError?.errorAt
  if (typeof fromAtom === 'number') return fromAtom
  const ts = Date.parse(action.lastInvokedAt ?? action.updatedAt)
  return Number.isNaN(ts) ? null : ts
}

export function ReconnectButton({
  action,
  stopPropagation,
  floating = false,
}: {
  action: StagedAction
  stopPropagation?: boolean
  /** Renders for a bar that floats over content, so the fills have to be opaque. */
  floating?: boolean
}) {
  const connectProvider = useConnectProvider()
  const { data: connections } = useConnections()
  const { invokeStagedAction } = useStagedActionCallbacks()
  const invokeError = useAtomValue(invokeErrorByIdAtom(action.id))
  const isRetrying = useAtomValue(isInvokingByIdAtom(action.id))

  const retryIsPrimary = useMemo(() => {
    const errorTs = getActionErrorUpdatedAt(action, invokeError)
    const connectionTs = getLatestConnectionUpdatedAt(connections, action.toolSet ?? null)
    return !!errorTs && !!connectionTs && connectionTs > errorTs
  }, [action, connections, invokeError])
  const primaryAction: ConnectionAction = retryIsPrimary ? 'retry' : 'reconnect'
  const alternateAction: ConnectionAction = retryIsPrimary ? 'reconnect' : 'retry'

  const runAction = useCallback(
    (target: ConnectionAction) => {
      if (target === 'retry') {
        void invokeStagedAction(action.id).catch((error) => {
          console.error('Unable to retry staged action after reconnect:', error)
        })
        return
      } else {
        void connectProvider(action.toolSet ?? null)
        return
      }
    },
    [action, invokeStagedAction, connectProvider],
  )

  const handleClick = useCallback(
    (target: ConnectionAction) => (e: MouseEvent) => {
      if (stopPropagation) e.stopPropagation()
      runAction(target)
    },
    [stopPropagation, runAction],
  )

  return (
    // Floating over the thread it takes the footer's outline look on an opaque danger fill, like
    // the Retry beside it; in a cell it is the solid.
    <ButtonGroup variant={floating ? 'outline' : 'primary'} tone="danger">
      <Button
        onClick={handleClick(primaryAction)}
        className={floating ? FLOATING_DANGER_FILL_CLASS : undefined}
        disabled={isRetrying}
        loading={isRetrying}
        left={ACTION_CONFIG[primaryAction].icon}
      >
        {ACTION_CONFIG[primaryAction].label}
      </Button>

      <Menu>
        <Menu.Trigger
          shape="square"
          className={floating ? FLOATING_DANGER_FILL_CLASS : undefined}
          disabled={isRetrying}
        >
          <ChevronDown className="size-2" />
        </Menu.Trigger>
        <Menu.Content align="end">
          <Menu.Item onClick={handleClick(alternateAction)} disabled={isRetrying}>
            {ACTION_CONFIG[alternateAction].icon}
            <span>{ACTION_CONFIG[alternateAction].label}</span>
          </Menu.Item>
        </Menu.Content>
      </Menu>
    </ButtonGroup>
  )
}
